import * as mp4boxModule from 'mp4box';
const MP4Box = (mp4boxModule as any).default || (mp4boxModule as any).MP4Box || mp4boxModule;

export interface DemuxerOptions {
  onConfig: (config: VideoDecoderConfig) => void;
  onChunk: (chunk: EncodedVideoChunk) => void | Promise<void>;
  onReady?: () => void;
  onError?: (error: string) => void;
}

export class MP4Demuxer {
  private onConfig: (config: VideoDecoderConfig) => void;
  private onChunk: (chunk: EncodedVideoChunk) => void | Promise<void>;
  private onReady: () => void;
  private onError: (error: string) => void;
  private file: any | null = null;
  private sourceUrl: string = '';
  private abortController = new AbortController();
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private chunkProcessing: Promise<void> = Promise.resolve();
  private aborted = false;

  constructor(url: string, options: DemuxerOptions) {
    this.sourceUrl = url;
    this.onConfig = options.onConfig;
    this.onChunk = options.onChunk;
    this.onReady = options.onReady || (() => {});
    this.onError = options.onError || (() => {});
  }

  async initialize(): Promise<void> {
    this.file = MP4Box.createFile();
    this.file.onError = (error: string) => {
      this.onError(`MP4Box error: ${error}`);
    };

    this.file.onReady = (info: any) => {
      const videoTrack = info.videoTracks[0];
      if (!videoTrack) {
        this.onError('No video track found');
        return;
      }

      let codec = videoTrack.codec;
      if (codec.startsWith('vp08')) codec = 'vp8';
      if (codec.startsWith('vp09')) codec = 'vp09.00.10.08';
      if (codec.startsWith('mp4v')) codec = 'mp4v.20.8';

      // info.videoTracks only contains metadata. We must pull the real TrakBox to find mdia!
      const trak = this.file.getTrackById(videoTrack.id);
      const description = this.extractExtradata(trak);

      const config: VideoDecoderConfig = {
        codec: codec,
        codedHeight: videoTrack.video.height,
        codedWidth: videoTrack.video.width,
        description: description,
        hardwareAcceleration: 'prefer-hardware'
      };

      this.onConfig(config);

      this.file.setExtractionOptions(videoTrack.id, null, { nbSamples: 1 });
      this.file.start();
      this.onReady();
    };

    this.file.onSamples = (trackId: number, ref: any, samples: any[]) => {
      for (const sample of samples) {
        const type = sample.is_sync ? 'key' : 'delta';
        const chunk = new EncodedVideoChunk({
          type: type,
          timestamp: (sample.cts * 1_000_000) / sample.timescale,
          duration: (sample.duration * 1_000_000) / sample.timescale,
          data: sample.data
        });
        this.chunkProcessing = this.chunkProcessing.then(async () => {
          if (this.aborted) {
            return;
          }
          await this.onChunk(chunk);
        });
      }
    };

    await this.fetchStream();
  }

  abort(): void {
    if (this.aborted) {
      return;
    }
    this.aborted = true;
    this.abortController.abort();
    void this.reader?.cancel().catch(() => {});
    this.file?.stop?.();
  }

  private extractExtradata(track: any): Uint8Array | undefined {
    let description: Uint8Array | undefined;
    const entries = track.mdia?.minf?.stbl?.stsd?.entries || [];
    for (const box of entries) {
      const avcC = box.avcC || box.hvcC || box.vpcC || box.av1C;
      if (avcC) {
        const stream = new MP4Box.DataStream(undefined, 0, MP4Box.DataStream.BIG_ENDIAN);
        avcC.write(stream);
        // stream.buffer might be padded. We want exactly position - 8 bytes.
        description = new Uint8Array(stream.buffer, 8, stream.position - 8); 
        console.log('[MP4Demuxer] Extracted description length:', description.length);
        break;
      }
    }
    if (!description) {
      console.warn('[MP4Demuxer] No description (Extradata) found in MP4 track!', entries);
    }
    return description;
  }

  private async fetchStream(): Promise<void> {
    const response = await fetch(this.sourceUrl, { signal: this.abortController.signal });
    if (!response.ok) {
      throw new Error(`MP4 request failed: ${response.status}`);
    }
    if (!response.body) {
      throw new Error('ReadableStream not supported in this browser');
    }
    const reader = response.body.getReader();
    this.reader = reader;
    let offset = 0;

    // Iterative loop instead of recursion to avoid stack overflow on large files
    // and to be compatible with Safari's stricter call-stack limits.
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        this.file?.flush();
        await this.chunkProcessing;
        break;
      }
      if (this.aborted) {
        break;
      }
      const buffer = value.buffer.slice(
        value.byteOffset,
        value.byteOffset + value.byteLength
      ) as ArrayBuffer & { fileStart: number };
      buffer.fileStart = offset;
      offset += value.byteLength;
      this.file?.appendBuffer(buffer);
      await this.chunkProcessing;
    }
    this.reader = null;
  }
}
